import { getThumbnailUrl } from "../../utils/urlConfig";
import React, { useState } from "react";
import styles from "./LocationsTree.module.css";
import { FaChevronDown, FaChevronRight, FaArchive, FaInfoCircle } from "react-icons/fa";

/**
 * LocationsTree Component
 * Renders a hierarchical tree view of containers (locations).
 */
const LocationsTree = ({ locations, onViewDetail }) => {
    // Build tree structure from flat locations list
    const buildTree = (data) => {
        const map = {};
        data.forEach((loc) => {
            map[loc.id] = { ...loc, children: [] };
        });

        const root = [];
        data.forEach((loc) => {
            const parentId = loc.parentLocation?.id;
            if (parentId && map[parentId]) {
                map[parentId].children.push(map[loc.id]);
            } else {
                root.push(map[loc.id]);
            }
        });

        return root;
    };

    const treeData = buildTree(locations);

    return (
        <div className={styles.treeContainer}>
            {treeData.length > 0 ? (
                treeData.map((node) => (
                    <TreeNode key={node.id} node={node} onViewDetail={onViewDetail} depth={0} />
                ))
            ) : (
                <p className={styles.noData}>No hierarchy found for the current items.</p>
            )}
        </div>
    );
};

/**
 * TreeNode Component
 * Recursive component to render each branch of the container tree.
 */
const TreeNode = ({ node, onViewDetail, depth }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;

    const toggleExpand = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={styles.nodeWrapper}>
            <div
                className={styles.nodeRow}
                style={{ paddingLeft: `${depth * 24}px` }}
                onClick={toggleExpand}
            >
                <div className={styles.iconContainer}>
                    {hasChildren ? (
                        isExpanded ? <FaChevronDown className={styles.toggleIcon} /> : <FaChevronRight className={styles.toggleIcon} />
                    ) : (
                        <div className={styles.emptySpacer} />
                    )}
                </div>

                <div className={styles.thumbnailContainer}>
                    {node.image ? (
                        <img
                            src={getThumbnailUrl(node.image)}
                            className={styles.thumbnail}
                            alt={node.name}
                        />
                    ) : (
                        <div className={styles.thumbnailPlaceholder}>
                            <FaArchive />
                        </div>
                    )}
                </div>

                <div className={styles.nodeContent}>
                    <span className={styles.nodeName}>{node.name}</span>
                    <span className={styles.nodeId}>#{node.id}</span>
                    {node.description && <span className={styles.nodeMeta}>{node.description}</span>}
                </div>

                <div className={styles.actions}>
                    <button
                        className={styles.viewButton}
                        onClick={(e) => { e.stopPropagation(); onViewDetail(node.id); }}
                        title="View Details"
                    >
                        <FaInfoCircle />
                    </button>
                </div>
            </div>

            {hasChildren && isExpanded && (
                <div className={styles.childrenWrapper}>
                    {node.children.map((child) => (
                        <TreeNode key={child.id} node={child} onViewDetail={onViewDetail} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default LocationsTree;
